import { Button, Dialog, TextInput, Typography, toast } from '@neo4j-ndl/react';
import { CheckIconOutline } from '@neo4j-ndl/react/icons';
import { fleetManagementApi, useActiveProject } from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';
import { ConnectionUrlInput } from '@nx/ui/src/connection-modal/connection-url-input';
import { useForm, useWatch } from 'react-hook-form';

import { validateSecureProtocol } from '../update-connection-url-modal';
import type { StepComponentProps } from './connect-deployment-wizard-modal';

interface CreateDeploymentFormValues {
  selectedConnectionType: 'monitor' | 'url';
  deploymentName: string;
  connectionUrl: string;
}

export const CreateDeploymentStepComponent = ({
  setActiveStepIndex,
  stop,
  sharedState,
  updateSharedState,
}: StepComponentProps) => {
  const activeProject = useActiveProject();
  const [createDeployment, { isLoading }] = fleetManagementApi.useCreateDeploymentMutation();

  const { control, register, handleSubmit, setValue, formState } = useForm<CreateDeploymentFormValues>({
    defaultValues: {
      selectedConnectionType: 'monitor',
      deploymentName: '',
      connectionUrl: '',
    },
    mode: 'onChange',
  });

  const selectedConnectionTypeField = register('selectedConnectionType', { required: true });
  const selectedConnectionType = useWatch({ control, name: selectedConnectionTypeField.name });
  const urlField = register('connectionUrl', {
    validate: (value) => {
      if (isNonEmptyString(value)) {
        return validateSecureProtocol(value);
      }
      // If connection type is not url and value is empty, return true (valid)
      return selectedConnectionType !== 'url';
    },
  });
  const nameField = register('deploymentName', {
    required: true,
    minLength: {
      value: 3,
      message: 'Minimum 3 characters to max 30 characters',
    },
    maxLength: {
      value: 30,
      message: 'Minimum 3 characters to max 30 characters',
    },
  });

  const connectionUrl = useWatch({ control, name: urlField.name });

  const onSubmit = async (data: CreateDeploymentFormValues) => {
    await createDeployment({
      project_id: activeProject.id,
      name: data.deploymentName,
      connection_url: isNonEmptyString(data.connectionUrl) ? data.connectionUrl : undefined,
    })
      .then(({ error, data: deploymentData }) => {
        if (error) {
          toast.danger('Failed to add deployment', { shouldAutoClose: true });
        } else {
          toast.success('Deployment added', { shouldAutoClose: true });
          updateSharedState({
            ...sharedState,
            deploymentId: deploymentData,
          });
          if (data.selectedConnectionType === 'monitor') {
            setActiveStepIndex(0);
          } else {
            stop();
          }
        }
      })
      .catch((err: Error) => {
        toast.danger(err.message);
      });
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
    >
      <Dialog.Content>
        <div className="flex flex-col gap-4">
          <div className="flex w-auto flex-row gap-4">
            <div
              className={`bg-palette-neutral-bg-weak w-1/2 cursor-pointer rounded-lg border-2 p-4 ${
                selectedConnectionType === 'monitor'
                  ? 'border-palette-primary-border-strong'
                  : 'border-palette-neutral-border-weak'
              }`}
              onClick={() => setValue(selectedConnectionTypeField.name, 'monitor')}
              {...selectedConnectionTypeField}
            >
              <div className="flex h-full flex-col justify-between">
                <div className="flex flex-col gap-2">
                  <Typography variant="subheading-large" className="mb-2">
                    Monitor deployment
                  </Typography>
                  <div className="flex flex-row gap-2">
                    <CheckIconOutline className="n-size-token-7 text-palette-primary-icon" />
                    <Typography variant="body-medium">Get access to topology and status of the deployment</Typography>
                  </div>
                  <div className="flex flex-row gap-2">
                    <CheckIconOutline className="n-size-token-7 text-palette-primary-icon" />
                    <Typography variant="body-medium">Get access to monitoring and system health</Typography>
                  </div>
                  <div className="flex flex-row gap-2">
                    <CheckIconOutline className="n-size-token-7 text-palette-primary-icon" />
                    <Typography variant="body-medium">Connect with database user</Typography>
                  </div>
                </div>
                <Typography variant="body-medium" className="text-palette-neutral-text-weak mt-2">
                  Continue to setup in 3 steps
                </Typography>
              </div>
            </div>
            <div
              className={`bg-palette-neutral-bg-weak w-1/2 cursor-pointer rounded-lg border-2 p-4 ${
                selectedConnectionType === 'url'
                  ? 'border-palette-primary-border-strong'
                  : 'border-palette-neutral-border-weak'
              }`}
              onClick={() => setValue(selectedConnectionTypeField.name, 'url')}
              {...selectedConnectionTypeField}
            >
              <div className="flex h-full flex-col justify-between">
                <div className="flex flex-col gap-2">
                  <Typography variant="subheading-large">URL Connection</Typography>
                  <div className="flex flex-row gap-2">
                    <CheckIconOutline className="n-size-token-7 text-palette-primary-icon" />
                    <Typography variant="body-medium">Connect to the deployment using the URL</Typography>
                  </div>
                </div>
                <Typography variant="body-medium" className="text-palette-neutral-text-weak">
                  Finish and exit dialog immediately
                </Typography>
              </div>
            </div>
          </div>
          <TextInput
            {...nameField}
            isFluid
            size="medium"
            label="Name"
            helpText="Name to identify the deployment."
            errorText={formState.errors.deploymentName?.message}
            htmlAttributes={{
              name: nameField.name,
            }}
          />
          <ConnectionUrlInput
            {...urlField}
            errorText={formState.errors.connectionUrl?.message}
            isReadOnly={false}
            isOptional={selectedConnectionType === 'monitor'}
            value={connectionUrl}
            onValueChange={(value) => setValue(urlField.name, value)}
          />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          fill="outlined"
          color="neutral"
          onClick={() => {
            stop();
          }}
        >
          Cancel
        </Button>
        <Button fill="filled" color="primary" type="submit" isLoading={isLoading} isDisabled={!formState.isValid}>
          Add
        </Button>
      </Dialog.Actions>
    </form>
  );
};
